<template>
    <div>
        <notifications position="bottom right" animation-type="css" />
        <div class="header">
            <div class="app-bar-title">
                <a href="https://dimitra.io">
                    <img  src="https://dimitra.io/wp-content/themes/dimitra/assets/imgs/dimitralogo2023.svg"/>
                </a>
            </div>

            <div class="header-title">
                <img src="/img/eu.png" class="eu-img">
                <span>{{ $t('dueDiligence.eudrDueDiligenceCompliance') }}</span>
            </div>
        </div>

        <v-container fluid class="hero-bg">
            <v-container class="hero">
                <v-row style="max-width: 100%;">
                    <v-col cols="6" sm="8" md="8" class="large-text-box">
                        <h1 class="large-text">
                            <i18n path="dueDiligence.publicReportHeroHeading" tag="span">
                                <br>
                                <br>
                            </i18n>
                        </h1>
                    </v-col>
                    <v-col cols="6" sm="6"  md="4">
                        <v-img class="hero-img" :src="logo"></v-img>
                    </v-col>
                </v-row>
            </v-container>

            <div class="compliance-sticker" v-if="report.status.toLowerCase() === 'compliant'">
                <v-img src="/img/eudr-dds/eudr-compliant.png"></v-img>
            </div>
        </v-container>

        <v-container class="conceal-overflow">
            <v-row>
                <v-col cols="12" sm="12" xs="12" md="6">
                    <v-card class="card">
                        <v-card-title class="card-title">
                            {{ $t('dueDiligence.dueDiligenceReportInformation') }}
                        </v-card-title>
                        <v-card-text class="card-text">
                            <v-row class="reportLine" v-for="info in reportInformation" v-bind:key="info.value">
                                <v-col style="text-align: left;">{{ info.title }}</v-col>
                                <v-col style="text-align: right; font-weight: 900">{{ report[info.value] || "N/A" }}</v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="12" xs="12" md="6">
                    <v-card class="card">
                        <v-card-title class="card-title">
                            {{ $t('dueDiligence.commoditiesAffectedByEudr') }}
                        </v-card-title>
                        <v-card-text>
                            <div class="commodity-tiles">
                                <v-card :class="(tile === report.type?.toLowerCase()) ? 'commodity-tile active': 'commodity-tile'" v-for="tile in tiles" :key="tile">
                                    <v-img :src="'/img/eudr-dds/eudr-' + tile + '.png'"></v-img>
                                    <p style="text-transform: capitalize;">{{ tile }}</p>
                                </v-card>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <div v-if="report.transaction">
                <div class="lined-header"><span>{{ $t('dueDiligence.blockchainVerification') }}</span></div>

                <v-row class="pa-2rem">
                    <v-col cols="12" md="4" sm="12">
                        <qrcode :text="report.transaction.link"></qrcode>
                    </v-col>
                    <v-col cols="12" md="6" sm="12" class="blockchain-text">
                        <p>{{ $t('dueDiligence.scanQrBlockchain') }}</p>
                        <p><a :href="report.transaction.link">{{ $t('dueDiligence.viewReportOnBlockChain') }}</a></p>
                        <p style="word-break: break-all">{{ report.transaction.transactionHash }}</p>
                    </v-col>
                </v-row>
            </div>

            <div class="lined-header"><span>{{ $t('dueDiligence.farmLocations') }}</span></div>

            <v-row>
                <v-col>
                    <div class="google-map" style="width:100%;height:50vh; z-index: 0;" id="map" ref="googleMap">
                    </div>
                </v-col>
            </v-row>

            <div class="lined-header"><span>{{ $t('dueDiligence.downloadFarmGeofences') }}</span></div>

            <v-card class="card" style="cursor: pointer;" @click="downloadGeoJSON">
                <v-row>
                    <v-col>
                        <p>{{ $t('dueDiligence.farmGeofenceGeojsonFile') }}</p>
                    </v-col>
                    <v-col style="text-align: right;">
                        <v-icon color="primary">mdi-download</v-icon>
                    </v-col>
                </v-row>
            </v-card>

            <div class="lined-header"><span>{{ $t('dueDiligence.eudrDueDiligenceReportSummary') }}</span></div>

            <v-row>
                <v-col cols="12" md="4" sm="12" v-for="(metric, i) in metricCards" v-bind:key="i">
                    <v-card class="status-card">
                        <v-card-title class="word-break">{{ metric.label }}</v-card-title>
                        <v-card-text>{{ metric.value }}</v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!--
              resultVersion 2 reports carry a binary Risk Detected / Zero-Negligible
              status, so the granular 7-card breakdown reads all-zero. Show a
              risk-vs-zero farm-count chart for those; keep the cards for v1.
            -->
            <template v-if="isV2Report">
                <v-row>
                    <v-col
                        cols="12"
                        md="4"
                        sm="12"
                        v-for="cat in deforestationChartCategories"
                        v-bind:key="cat.key"
                    >
                        <v-card :class="'status-card ' + (cat.key === 'zeroNegligible' ? 'zero' : cat.key === 'manuallyMitigated' ? 'low' : 'very-high')">
                            <v-card-title class="word-break">{{ cat.label }}</v-card-title>
                            <v-card-text>
                                <v-row justify="space-between">
                                    <v-col cols="6">
                                        <small class="metric-label">{{ $t('dueDiligence.totalFarmsCount') }}</small>
                                        <h3 class="metric-text">{{ deforestationBuckets[cat.key].farms }}</h3>
                                    </v-col>
                                    <v-col cols="6">
                                        <small class="metric-label">{{ $t('dueDiligence.totalArea') }}</small>
                                        <h3 class="metric-text">{{ (deforestationBuckets[cat.key].area / 2.47105).toFixed(4) }} ha</h3>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <v-card class="status-card">
                            <v-card-title class="word-break">
                                {{ $t('dueDiligence.deforestationRiskBreakdown') || 'Deforestation Risk Breakdown' }}
                            </v-card-title>
                            <v-card-text>
                                <apexchart
                                    type="bar"
                                    height="320"
                                    :options="deforestationChartOptions"
                                    :series="deforestationChartSeries"
                                />
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </template>

            <v-row v-else>
                <v-col cols="12" md="4" sm="12" v-for="(metric, i) in deforestationCards" v-bind:key="i">
                    <v-card :class="'status-card ' + metric.color">
                        <v-card-title class="word-break">{{ metric.label }}</v-card-title>
                        <v-card-text>
                            <v-row justify="space-between">
                                <v-col cols="6">
                                    <small class="metric-label">{{ $t('dueDiligence.totalFarmsCount') }}</small>
                                    <h3 class="metric-text">{{ metric.farms }}</h3>
                                </v-col>
                                <v-col cols="6">
                                    <small class="metric-label">{{ $t('dueDiligence.totalArea') }}</small>
                                    <h3 class="metric-text">{{  (metric.area / 2.47105).toFixed(4) }} ha</h3>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Commented until futhter logic is defined and implemented -->
            <!-- <div class="lined-header"><span>EUDR Protocol Verification Report</span></div>

            <v-row justify="space-between">
                <v-col cols="6" v-for="protocol in protocols" v-bind:key="protocol">
                    <v-card class="protocol-card">
                        <v-row>
                            <v-col cols="1">
                                <v-icon :class="report.status === 'Compliant' ? 'green': 'red'">
                                    {{ report.status === 'Compliant' ? 'mdi-check-circle': 'mdi-close-circle' }}
                                </v-icon>
                            </v-col>
                            <v-col cols="11" style="font-weight: 900;">{{ protocol }}</v-col>
                        </v-row>
                    </v-card>
                </v-col>
            </v-row> -->

            <!--
              Assessment questions and answers, as frozen when the DD was issued.
              Only populated for an issued report; nothing renders otherwise.
            -->
            <template v-if="assessmentResponses.length">
                <div class="lined-header"><span>{{ $t('dueDiligence.riskAssessment') }}</span></div>

                <div
                    v-for="(assessment, ai) in assessmentResponses"
                    :key="'assessment-' + ai"
                    class="mb-8"
                >
                    <v-row class="mb-2" align="center">
                        <v-col cols="12" md="8">
                            <span style="font-weight: 900;">
                                {{ assessmentFarmName(assessment) }}
                            </span>
                        </v-col>
                        <v-col cols="12" md="4" class="text-md-right">
                            <span :class="assessmentStatusClass(assessment.riskAssessmentStatus)">
                                {{ assessmentStatusLabel(assessment.riskAssessmentStatus) }}
                            </span>
                        </v-col>
                    </v-row>

                    <v-simple-table>
                        <template v-slot:default>
                            <thead>
                                <tr>
                                    <th class="text-left">{{ $t('dueDiligence.question') }}</th>
                                    <th class="text-left">{{ $t('dueDiligence.answer') }}</th>
                                    <th class="text-right">{{ $t('deforestation.riskLevel') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(question, qi) in assessment.questions" :key="'q-' + ai + '-' + qi">
                                    <td>{{ question.title }}</td>
                                    <td>{{ answerText(question) }}</td>
                                    <td class="text-right">
                                        <span :class="riskClass(question)">{{ riskText(question) }}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </template>
                    </v-simple-table>
                </div>
            </template>
        </v-container>

        <v-footer padless class="footer">
            <v-container>

                <v-row>
                    <v-col cols="12" md="9" sm="12">
                        <p class="footer-title">{{ $t('dueDiligence.signatoryCompany') }}</p>
                     <v-row>
                      <v-col cols="12" xs="12" md="4" v-for="(links, i) in footer" v-bind:key="i">
                            <p v-for="link in links" v-bind:key="link.label">
                                <a :href="link.href">{{ link.label }}</a>
                            </p>
                        </v-col>
                      </v-row>  
                    </v-col>

                    <v-col cols="12" md="3" sm="12">
                        <p class="footer-title mrb-20">{{ $t('dueDiligence.community') }}</p>
                        <p>
                            <a href="social.link" class="social" v-for="social in socials" v-bind:key="social.icon">
                                <v-icon class="white">{{ 'mdi-' + social.icon }}</v-icon>
                            </a>
                        </p>
                        <p>
                            <a href="https://dimitra.io/news/">{{ $t('dueDiligence.news') }}</a>
                        </p>
                    </v-col>
                </v-row>
                <v-row no-gutters justify="start">
                    <v-col cols="12" md="12">
                        <p><a href="#">&copy; Dimitra Incorporated</a></p>
                    </v-col>
                </v-row>
            </v-container>
        </v-footer>
    </div>
</template>

<script>
import DeforestationService from '@/_services/DeforestationService';
import DownloadMixin from '@/mixins/DownloadMixin';
import { countries } from '@/constants/countries.js'
import LoadingMixin from '@/mixins/LoadingMixin'
import { getGoogleMapsLoader } from '@/mixins/GoogleMapLoaderSingleton';
import { ensureClosedPolygonRing, rewindGeoJson } from '../../../geo-utils';
import { isGeoJSONObject } from 'geojson-validation';
import {
    AssessmentAnswerRisk,
    assessmentStatusBadge,
    classifyOptionValue,
    riskColorClass,
    riskLabel,
    worstRisk,
} from '@/utils/assessmentRisk';

export default {
    computed: {
        /** Categories shown in the risk-vs-zero chart; mitigated only if any exist. */
        deforestationChartCategories() {
            const cats = [
                { key: 'riskDetected', label: this.$t('dueDiligence.riskDetected') || 'Risk Detected', color: '#B71C1C' },
                { key: 'zeroNegligible', label: this.$t('dueDiligence.zeroNegligibleRisk') || 'Zero/Negligible', color: '#2E7D32' },
            ];
            if (this.deforestationBuckets.manuallyMitigated.farms > 0) {
                cats.push({ key: 'manuallyMitigated', label: this.$t('dueDiligence.manuallyMitigated') || 'Manually Mitigated', color: '#1565C0' });
            }
            return cats;
        },
        deforestationChartSeries() {
            return [{
                name: this.$t('dueDiligence.totalFarmsCount') || 'Total Farms',
                data: this.deforestationChartCategories.map((c) => this.deforestationBuckets[c.key].farms),
            }];
        },
        deforestationChartOptions() {
            const cats = this.deforestationChartCategories;
            return {
                chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
                plotOptions: { bar: { distributed: true, borderRadius: 4, columnWidth: '45%', dataLabels: { position: 'top' } } },
                colors: cats.map((c) => c.color),
                dataLabels: {
                    enabled: true,
                    offsetY: -20,
                    style: { colors: ['#304758'], fontSize: '14px', fontWeight: 600 },
                },
                legend: { show: false },
                xaxis: { categories: cats.map((c) => c.label), labels: { style: { fontSize: '13px' } } },
                yaxis: {
                    title: { text: this.$t('dueDiligence.totalFarmsCount') || 'Total Farms' },
                    labels: { formatter: (v) => Math.round(v) },
                    allowDecimals: false,
                },
                tooltip: {
                    y: {
                        formatter: (val, opts) => {
                            const cat = cats[opts.dataPointIndex];
                            const ha = (this.deforestationBuckets[cat.key].area / 2.47105).toFixed(4);
                            return `${val} farms · ${ha} ha`;
                        },
                    },
                },
            };
        },
        reportEncId() {
            return encodeURIComponent(this.pageParams[0]);
        },
        logo() {
            return "https://dimitra-prod-public-images.s3.amazonaws.com/org/" + this.pageParams[1].replace("_", ".");
        },
        pageParams() {
            return this.$route.params.reportEncId.split("___");
        }
    },
    async mounted() {
        await this.fetchReportData()
        // Loading google map and geofences
        try {
            const loader = getGoogleMapsLoader();
            const google = await loader.load();
            await this.initializeMap(google);
            if (!this.map) return;
            const bounds = new this.google.maps.LatLngBounds();
            this.productionPlaces.forEach(({ farm }) => {
                this.mapGeofences(farm).forEach((geofence) => {
                    // One malformed geofence must not stop the rest from drawing.
                    try {
                        const radius = geofence.geofenceRadius;

                        if (radius !== null && radius !== undefined) {
                            // Circle
                            this.createCircle(
                                Number.parseFloat(radius),
                                Number.parseFloat(geofence.geofenceCenterLat),
                                Number.parseFloat(geofence.geofenceCenterLog),
                                bounds
                            );
                        } else {
                            // Polygon
                            this.createPolygon(geofence.geofenceCoordinates, bounds);
                        }
                    } catch (e) {
                        console.log('Geofence render error', geofence.id, e);
                    }
                });
            });

            // Default center is fixed; frame the farms instead so they are visible
            // wherever in the world they are.
            if (!bounds.isEmpty()) {
                this.map.fitBounds(bounds);
            }

            this.loader = loader;
        } catch (e) {
            console.log('Loader Error', e);
        }
    },
    methods: {
        async fetchReportData(){
            try{
                this.startLoading()
                const data = await DeforestationService.fetchPublicReport(this.reportEncId);
                this.report = data.report;
                this.metrics = data.metrics;
                this.productionPlaces = data.productionPlaces;

                this.computeDeforestationBuckets();

                const user = data.report?.operator || data.report?.user || data.report.supplier
                this.report.eoriNumber = user.eori_number;


                // Get operator name
                if (this.report.operator) {
                    this.report.operator = `${this.report.operator.firstName} ${this.report.operator.lastName}`;
                }else if(this.report.user){
                    this.report.operator = `${this.report?.user?.firstName} ${this.report?.user?.lastName}`;
                }

                if (data.report.countryOfActivity instanceof Array) {
                    this.report.countryOfActivity = data.report.countryOfActivity[0];
                }

                if (data?.report?.transactions.length) {
                    this.report.transaction = data?.report?.transactions[0] 
                    this.report.transaction.link = data.blockchainLink;
                }

                if (data?.report?.transaction) {
                    this.report.transaction.link = data.blockchainLink;
                }


                this.report.type = this.report.product_detail?.name;

                this.metricCards = [
                    {
                        label: "Total Number of Farms",
                        value: this.metrics.totalProductionPlaces
                    },
                    {
                        label: "Total Area",
                        value: `${this.metrics.totalArea} ha`
                    },
                    {
                        label: "Total No. of Geofence Polygons",
                        value: this.metrics.polygonProductionPlaces
                    },
                    {
                        label: "Total No. of Geofence Points",
                        value: this.metrics.pointProductionPlaces
                    },
                    {
                        label: "Total No. of Geofence Assessments",
                        value: this.metrics.totalDeforestationAssessments
                    },
                    {
                        label: "Total No. of Risk Assessments",
                        value: this.report.requiredAssessment.length
                    }
                ];

                this.deforestationCards = [
                    {
                        label: "Very High Deforestation Probability",
                        color: "very-high"
                    },
                    {
                        label: "High Deforestation Probability",
                        color: "high"
                    },
                    {
                        label: "Medium Deforestation Probability",
                        color: "medium"
                    },
                    {
                        label: "Low Deforestation Probability",
                        color: "low"
                    },
                    {
                        label: "Very Low Deforestation Probability",
                        color: "very-low"
                    },
                    {
                        label: "Zero/Negligible Deforestation Probability",
                        color: "zero"
                    },
                    {
                        label: "Manually Mitigated",
                        color: "zero"
                    },
                ].map((card) => {
                    const details = data.deforestationDetails[card.label];
                    return {
                        ...card,
                        ...details
                    };
                });

                // Only present once the report has been issued and its answers
                // snapshotted; absent for drafts.
                this.assessmentResponses = data.assessmentResponses || [];

                this.stopLoading()
            }catch(e){
                this.stopLoading()
            }
        },
        /**
         * Collapse each production place's deforestation status into the binary
         * v2 buckets. Mirrors mapDeforestationStatusToNewStatus on the backend:
         * every non-zero probability band (and "Risk Detected") counts as risk,
         * zero/negligible as safe, and manually mitigated is called out on its own.
         * Returns null for unknown/NA so those farms are not miscounted.
         */
        classifyDeforestation(statusRaw) {
            const s = (statusRaw || '').toString().toLowerCase().trim();
            if (!s || s === 'na') return null;
            if (s.includes('zero') || s.includes('negligible')) return 'zeroNegligible';
            if (s.includes('manually mitigated')) return 'manuallyMitigated';
            if (
                s.includes('risk detected') ||
                s.includes('high') ||
                s.includes('medium') ||
                s.includes('low')
            ) {
                return 'riskDetected';
            }
            return null;
        },
        computeDeforestationBuckets() {
            const buckets = {
                riskDetected: { farms: 0, area: 0 },
                zeroNegligible: { farms: 0, area: 0 },
                manuallyMitigated: { farms: 0, area: 0 },
            };
            // The 6 granular long-form statuses the legacy (v1) cards are keyed on.
            // A farm that classifies into a bucket but whose raw status is NOT one of
            // these is v2/binary data ("Risk Detected", "Zero/Negligible Risk",
            // "Manually Mitigated", or any short "… Probability" form).
            const GRANULAR_V1 = new Set([
                'very high deforestation probability',
                'high deforestation probability',
                'medium deforestation probability',
                'low deforestation probability',
                'very low deforestation probability',
                'zero/negligible deforestation probability',
            ]);
            let sawV2Status = false;

            (this.productionPlaces || []).forEach((pp) => {
                if (pp && pp.removed) return;
                const status =
                    (pp && pp.productionPlaceDeforestationInfo && pp.productionPlaceDeforestationInfo.deforestationStatus) ||
                    (pp && pp.deforestationStatus) ||
                    (pp && pp.eudr_deforestation_status) ||
                    null;

                const key = this.classifyDeforestation(status);
                if (!key) return;

                if (!GRANULAR_V1.has((status || '').toString().toLowerCase().trim())) {
                    sawV2Status = true;
                }

                const area = Number((pp && pp.farm && pp.farm.area) || 0) || 0;
                buckets[key].farms += 1;
                buckets[key].area += area;
            });

            this.deforestationBuckets = buckets;
            this.isV2Report = sawV2Status;
        },
        /** Farm label for an assessment block; report-level blocks cover every farm. */
        assessmentFarmName(assessment) {
            if (!assessment.productionPlaceId) return this.$t('deforestation.allFarms');
            const place = this.productionPlaces.find(
                (p) => Number(p.id) === Number(assessment.productionPlaceId),
            );
            return (place && place.farm && place.farm.farmName) || `#${assessment.productionPlaceId}`;
        },
        assessmentStatusLabel(status) {
            return assessmentStatusBadge(status).label;
        },
        assessmentStatusClass(status) {
            return `assessment-status-${assessmentStatusBadge(status).color}`;
        },
        /** Selected option labels, falling back to a free-text answer. */
        answerText(question) {
            const answers = (question.answers || [])
                .map((a) => a.label)
                .filter(Boolean);
            if (answers.length) return answers.join(', ');
            return question.textAnswer !== null && question.textAnswer !== undefined
                ? String(question.textAnswer)
                : '';
        },
        /**
         * Risk badge for an answer. Uses the tier frozen on the snapshot so the
         * published page still reads correctly if the option is edited later.
         */
        questionRisk(question) {
            const frozen = (question.risk || []).filter(
                (r) => r && r !== AssessmentAnswerRisk.UNCLASSIFIED,
            );
            if (frozen.length) return worstRisk(frozen);
            return worstRisk((question.answers || []).map((a) => classifyOptionValue(a.riskValue)));
        },
        riskText(question) {
            return riskLabel(this.questionRisk(question));
        },
        riskClass(question) {
            return riskColorClass(this.questionRisk(question));
        },
        roundUpToXixDecimal(input){
            return Math.ceil(Number(input) * 1e6) / 1e6;
        },
        acreToHectare(acreValue){
            const hect = Number(acreValue) / 2.471
            return Number(hect.toFixed(6))
        },
        removeDuplicateCoordinatesForPolygon(coordinates) {
            if (coordinates.length < 3) {
                return coordinates;
            }

            const uniqueCoordinates = [];
            const seenCoordinates = new Set();

            
            uniqueCoordinates.push(coordinates[0]);
            seenCoordinates.add(`${coordinates[0][0]},${coordinates[0][1]}`);

            
            for (let i = 1; i < coordinates.length - 1; i++) {
                const [lat, lon] = coordinates[i];
                const key = `${lat},${lon}`;
                if (!seenCoordinates.has(key)) {
                    seenCoordinates.add(key);
                    uniqueCoordinates.push([lat, lon]);
                }
            }
            uniqueCoordinates.push(coordinates[coordinates.length - 1]);
            return uniqueCoordinates;
        },
        createMarker(lat, lng, type) {
            const iconBlue = 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png';
            const iconPurple = 'http://maps.google.com/mapfiles/ms/icons/purple-dot.png';
            let icon = type == 'polygon' ? iconPurple : iconBlue;

            const latLng = new this.google.maps.LatLng(lat, lng);
            const marker = new this.google.maps.Marker({
                position: latLng,
                map: this.map,
                icon
            });

            this.markers.push(marker);
            return marker;
        },
        /**
         * Geofences to draw for a farm: live primary ones, matching the report
         * metrics. Falls back to any live geofence if none is flagged primary.
         */
        mapGeofences(farm) {
            const live = ((farm && farm.GeoFences) || []).filter((g) => !g.is_deleted);
            const primary = live.filter((g) => Number(g.isPrimary) === 1);
            return primary.length ? primary : live;
        },
        createPolygon(coordinates, bounds) {
            const paths = (coordinates || [])
                .map(x => ({ lat: Number.parseFloat(x.lat), lng: Number.parseFloat(x.log) }))
                .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));
            if (paths.length < 3) return;

            const polygon = new this.google.maps.Polygon({
                paths,
                strokeColor: "#A93EEA",
                strokeOpacity: 0.8,
                strokeWeight: 3,
                map: this.map,
                fillColor: "#A93EEA",
                fillOpacity: 0.5,
                editable: false,
                draggable: false
            });

            this.createMarker(paths[0].lat, paths[0].lng, 'polygon');
            this.polygons.push(polygon);
            if (bounds) paths.forEach((p) => bounds.extend(p));
        },
        createCircle(radius = 0, lat = null, lng = null, bounds = null) {
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

            const circle = new this.google.maps.Circle({
                fillColor: '#566DE3',
                strokeColor: '#566DE3',
                fillOpacity: 0.8,
                strokeOpacity: 1,
                strokeWeight: 2,
                map: this.map,
                radius,
                editable: false,
                draggable: false
            });

            const marker = this.createMarker(lat, lng, 'point');
            circle.bindTo('center', marker, 'position');
            this.circles.push(circle);
            if (bounds) {
                const circleBounds = circle.getBounds();
                if (circleBounds) bounds.union(circleBounds);
                else bounds.extend({ lat, lng });
            }
        },
        downloadGeoJSON() {
            const geoJSON = {
                type: "FeatureCollection",
                features: []
            }
            this.productionPlaces.forEach(item => {
                const {farm} = item
                const feature = {
                        type: "Feature",
                        geometry: {},
                        properties:{}
                }

                const countryISO2Code = countries.filter(x => 
                    x.Code.toLowerCase() == farm?.userDdsAssoc?.countryId?.toLowerCase() || 
                    x.Name.toLowerCase() == farm?.userDdsAssoc?.countryId?.toLowerCase()
                ) 
                feature.properties["ProducerName"] = `${farm?.userDdsAssoc?.firstName} ${farm?.userDdsAssoc?.lastName}`
                feature.properties["ProducerCountry"] = countryISO2Code.length ? countryISO2Code[0].Code : ""
                if(farm?.farmName && farm?.farmName?.trim()){
                    feature.properties["ProductionPlace"] = farm?.farmName  
                }

                if(item.farmType.toLowerCase() == 'point'){
                   feature.geometry["type"] = 'Point'
                   feature.geometry["coordinates"] = [
                     this.roundUpToXixDecimal(farm?.log),
                     this.roundUpToXixDecimal(farm?.lat)
                    ]
                    //Tempory fix for now
                    if(Number(farm?.area) != 9.8842){
                       feature.properties["Area"] = this.acreToHectare(farm?.area)
                    }
                }else{
                    feature.geometry["type"] = 'Polygon' 
                    const coordinates = farm?.FarmCoordinates?.map(x  => {
                        return [
                            this.roundUpToXixDecimal(x.log),
                            this.roundUpToXixDecimal(x.lat)
                        ]
                    });
                    const uniqueCoordinates = this.removeDuplicateCoordinatesForPolygon(coordinates);
                    // Ensure the polygon is closed by adding the first coordinate at the end if it's not already
                    ensureClosedPolygonRing(uniqueCoordinates); 
                    feature.geometry["coordinates"] = [uniqueCoordinates];
                }
                
                geoJSON.features.push(feature)
            })
            rewindGeoJson(geoJSON);
            if(!isGeoJSONObject(geoJSON)) {
                this.$notify({
                    type: 'error',
                    text: 'Failed to generate valid GeoJSON file.',
                });
                return;
            }
            this.downloadObject(JSON.stringify(geoJSON), `dimitra-geojson-${this.report.type}-${Date.now()}`, '.geojson');
        },
        initializeMap(google) {
            this.google = google;
            const mapContainer = this.$refs.googleMap;
            if (!mapContainer) return;
            const center = new this.google.maps.LatLng(this.mapOptions.center.lat, this.mapOptions.center.lng);

            this.map = new google.maps.Map(mapContainer, {
                mapTypeId: this.mapOptions.mapTypeId,
                zoom: this.mapOptions.zoom,
                center
            });
        },
    },
    data() {
        return {
            circle: null,
            google: null,
            map: null,
            loader: null,
            mapOptions: {
                mapTypeId: "satellite",
                center: {
                 lat: -2.5489, lng: 118.0149
                },
                zoom: 4,
            },
            metricCards: [],
            deforestationCards: [],
            productionPlaces: [],
            metrics: [],
            assessmentResponses: [],
            // resultVersion 2 reports collapse the granular probability bands into a
            // binary Risk Detected / Zero-Negligible status, so the 7 granular cards
            // all read 0. When v2 data is present we show a risk-vs-zero chart instead.
            isV2Report: false,
            deforestationBuckets: {
                riskDetected: { farms: 0, area: 0 },
                zeroNegligible: { farms: 0, area: 0 },
                manuallyMitigated: { farms: 0, area: 0 },
            },
            protocols: [
                "Farm Location Verification",
                "Farm Size and Geofence Verification",
                "EUDR Deforestation Assessment",
                "Regional Risk Assessment",
                "Risk Assessment & Mitigation",
                "Protected & Indigenous Area Verification",
                "No Invasion of Protected Areas",
                "Habitat and Species Management Area Verification",
                "Strict Natural Reserve Verification",
                "Protected Area with Sustainable Use of Natural Resources Verification",
                "Verification in Registry of Employers with slavery-like conditions",
                "Verification in Registration of Environmental Assessments and Embargoes"
            ],
            footer: [
                [
                    {
                        label: "Contact Us",
                        href: "https://dimitra.io/contact-us/"
                    },
                    {
                        label: "Case Studies",
                        href: "https://dimitra.io/case-studies"
                    }
                ],
                [
                    {
                        label: "Business",
                        href: "https://dimitra.io/?page_id=303"
                    },
                    {
                        label: "Careers",
                        href: "https://dimitra.io/careers/"
                    }
                ],
                [
                    {
                        label: "Terms and Conditions",
                        href: "https://dimitra.io/careers/"
                    },
                    {
                        label: "Privacy Policy",
                        href: "https://dimitra.io/privacy-policy-2/"
                    },
                    {
                        label: "FAQ",
                        href: "#"
                    }
                ]
            ],
            socials: [
                {
                    icon: "facebook",
                    link: "https://www.facebook.com/dimitratech"
                },
                {
                    icon: "send",
                    link: "https://t.me/dimitraofficialgroup"
                },
                {
                    icon: "twitter",
                    link: "https://twitter.com/dimitratech"
                },
                {
                    icon: "instagram",
                    link: "https://www.instagram.com/dimitratech/"
                },
                {
                    icon: "linkedin",
                    link: "https://www.linkedin.com/company/dimitra-technology"
                },
                {
                    icon: "facebook-workplace",
                    link: "https://coinmarketcap.com/currencies/dimitra/"
                }
            ],
            reportInformation: [
                {
                    title: "EUDR Reference Number",
                    value: 'EUDRReferenceNumber'
                },
                {
                    title: "Internal Reference Number",
                    value: 'internalReferenceNumber'
                },
                {
                    title: "Activity",
                    value: 'activity'
                },
                {
                    title: "Country of Activity",
                    value: 'countryOfActivity'
                },
                {
                    title: "Country of Entry",
                    value: 'countryOfEntry'
                },
                {
                    title: "Operator Name",
                    value: 'operator'
                },
                {
                    title: "EORI Number",
                    value: 'eoriNumber'
                },
                {
                    title: "Product",
                    value: 'type'
                },
                {
                    title: "Status",
                    value: 'status'
                },
            ],
            report: {
                EUDRReferenceNumber: "N/A",
                internalReferenceNumber: "N/A",
                activity: "N/A",
                countryOfActivity: "N/A",
                countryOfEntry: "N/A",
                operator: "N/A",
                eoriNumber: "",
                product: "N/A",
                status: "N/A",
                type: "N/A"
            },
            tiles: ['coffee', 'cattle', 'cocoa', 'soya', 'wood', 'palm-oil', 'rubber'],
            polygons: [],
            circles: [],
            markers: []
        }
    },
    mixins: [ DownloadMixin,LoadingMixin ]
}
</script>

<style lang="scss" scoped>
$primary: rgb(22,79,36);

* {
  font-family: "Poppins", sans-serif;
}

::v-deep .card-title {
  word-break: normal !important;
  white-space: normal !important;
  overflow-wrap: break-word !important;
  hyphens: none !important;
}

.mrb-20{
    margin-bottom: 20px !important;
}

@font-face {
	font-family: pop_light;
	src: url('/font/Poppins-Light.ttf');
}
@font-face {
	font-family: pop_regular;
	src: url('/font/Poppins-Regular.ttf');
}
@font-face {
	font-family: Poppins-Medium;
	src: url('/font/Poppins-Medium.ttf');
}
@font-face {
	font-family: pop_semiBold;
	src: url('/font/Poppins-SemiBold.ttf');
}
::v-deep .v-app-bar-title__content {
    width: auto;
}
.white{
    color:#fff;
}
::v-deep .header {
    padding: 2rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
}

::v-deep .eu-img {
    height: 50px;
    width: 50px;
}

::v-deep .hero-img {
    width: 40%;
    height: auto;
    margin: auto;
}

::v-deep .hero-bg {
    position: relative;
    background: 
        linear-gradient(rgba(86, 86, 86, 0.5), rgba(184, 184, 184, 0.5)), /* dark overlay */
        url(/img/eudr-dds/public-nkg-header.png);              /* actual image */
    background-size: cover;
    background-position: center;
    margin-bottom: 4rem;
}

::v-deep .hero {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 600px;
}

::v-deep .card-title {
    color: $primary;
    font-weight: bolder;
    text-transform: uppercase;
    font-size: 32px;
    justify-content: center;
    margin-bottom: 24px;
}

::v-deep .card-text {
    font-size: 1.2em;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    height: 80%;
    margin-top: 10%;
}

::v-deep .v-icon {
    &.green {
        color: #00BD73;
    }

    &.red {
        color: #DA0005;
    }
}

::v-deep .card {
    padding: 2rem;
    height: 100%;
    border-radius: 24px;
}

::v-deep .protocol-card {
    margin-bottom: 12px;
    padding: 16px;
}

::v-deep .status-card {
    padding-left: 1rem;
    border-radius: 12px;
    border-left-width: 8px !important;
    border-left-style: solid;

    &.very-high {
        border-left-color: #8B0000;
    }

    &.high {
        border-left-color: #DA0005;
    }

    &.medium {
        border-left-color: #F06B20;
    }

    &.low {
        border-left-color: #F7970F;
    }

    &.very-low {
        border-left-color: #F6CE2F;
    }

    &.zero {
        border-left-color: #00BD73;
    }
}

::v-deep .blockchain-text {
    font-size: 1.5em;
    font-weight: bold;

    & > p {
        margin-bottom: 16px;
    }
}

::v-deep .conceal-overflow {
    overflow: hidden;
}

.commodity-tiles {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 2rem 5%;
}

.word-break {
  word-break: normal !important;
  white-space: normal !important;
  overflow-wrap: break-word !important;
  hyphens: none !important;
}

::v-deep .commodity-tile {
    flex-grow: 0;
    padding: 1.5rem 2.5rem;
    border-radius: 24px;

    &.active {
        background-color: #469d16;
    }

    p {
        padding-top: 24px;
        text-align: center;
        font-size: 1.2em;
        font-weight: bolder;
    }
}

::v-deep .centered-row {
    margin: 0 300px;
}

.header-title {
    color: green;
    font-size: 36px;
    padding-left: 50px;
    margin-left: 50px;
    border-left: 4px solid green;
    display: flex;
    flex-direction: row;
    gap: 20px;
    font-weight: bolder;
}

.large-text {
    color: white;
    font-size: 4rem;
    font-weight: bolder;
}
.compliance-sticker {
    position: absolute;
    right: 0;
    bottom: 10%;
    padding: 0 2rem 1rem 1rem;
    background-color: white;
}

.lined-header {
  line-height: 0.5;
  text-align: center;
  margin: 8rem 0 6rem 0;
  color: $primary;
}

/* Risk assessment answer badges */
.very-high-color { color: #b71c1c; font-weight: 700; }
.high-color { color: #da3d3d; font-weight: 700; }
.medium-color { color: #ffa826; font-weight: 700; }
.low-color { color: #f2b705; font-weight: 700; }
.zero-color { color: #489e16; font-weight: 700; }

/* Farm-level assessment status */
.assessment-status-success { color: #489e16; font-weight: 700; }
.assessment-status-warning { color: #ffa826; font-weight: 700; }
.assessment-status-error { color: #da3d3d; font-weight: 700; }
.assessment-status-info { color: #2a6fd6; font-weight: 700; }
.assessment-status-grey { color: #616161; font-weight: 700; }
.lined-header span {
  display: inline-block;
  position: relative;
  font-size: 3em;
  line-height: 3rem;
  font-weight: bolder;
  text-transform: uppercase
}

.lined-header span:before,
.lined-header span:after {
  content: "";
  position: absolute;
  height: 5px;
  border-bottom: 3px solid lightgray;
  top: 8px;
  width: calc(100vw);
}

.lined-header span:before {
  right: 100%;
  margin-right: 30px;
}

.lined-header span:after {
  left: 100%;
  margin-left: 30px;
}

.footer {
    background: #0EBF67;
    background: linear-gradient(269.28deg, #0E693C -7.07%, #0EBF67 85.48%);
    padding: 5rem 0;
    margin-top: 4rem;

    .social {
        margin-right: 5px;
    }

    p {
        margin: 12px 0;

        a {
            color: white;
            text-decoration: none;
        }
    }

    .footer-title {
        color: white;
        font-weight: bolder;
        font-size: 1.2em;
    }
}

.pa-2rem{
  padding: 0 2rem;
}

@media (max-width:1120px) {
    ::v-deep .header {
    justify-content: center;
  }  
  
  ::v-deep .header-title {
    border-left: none !important;
    margin-left: 0;
  }
  ::v-deep .large-text {
    font-size: 3rem;
  }
}
@media (max-width:600px) {
   ::v-deep .header-title{
   font-size: 1.5rem !important;
  }
  ::v-deep .large-text{
    font-size: 2.5rem;
  }
  ::v-deep .lined-header span{
    font-size: 2rem;
    line-height: 2.3rem;
  }
}

@media (max-width:410px) {
    ::v-deep .header-title{
        margin-left: 0;
    }
    ::v-deep .large-text-box {
        margin-bottom: 2rem;
    }
}
@media(max-width:390px){
::v-deep .compliance-sticker{
    bottom: 7%; 
}
}
</style>